import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { USE_SEO_BASE } from '../core/http/http-context.tokens';
import { NotificationService } from '../core/services/notification.service';
import { LocalStorageService } from '../core/services/local-storage.service';
import { CoreTargetService } from '../core/services/target.service';
import { FORCE_REGION } from '../core/http/http-context.tokens';

@Injectable({
  providedIn: 'root',
})
export class WebsiteAuditService {
  constructor(
    private http: HttpClient,
    private router: Router,
    private notificationService: NotificationService,
    private localStorageService: LocalStorageService,
    private targetService: CoreTargetService,
  ) {}

  private auditTasks = new BehaviorSubject<any>([]);
  getAuditTasks() {
    return this.auditTasks.asObservable();
  }
  updateAuditTasks(tasks: any) {
    this.auditTasks.next(tasks);
  }
  fetchAuditTasks() {
    function getDomainFromUrl(url: string): string | null {
      try {
        const normalized = url.startsWith('http') ? url : `https://${url}`;

        const { hostname } = new URL(normalized);
        return hostname.replace(/^www\./, '');
      } catch {
        return null;
      }
    }

    this.getAuditTask().subscribe({
      next: (res) => {
        (res || []).forEach((element: any) => {
          element['domain'] = getDomainFromUrl(element.url);
        });

        this.updateAuditTasks(res);
      },
      error: (err) => {
        this.notificationService.error(err?.error?.message);
      },
    });
  }

  private auditTasksLoading = new BehaviorSubject<any>(false);
  getAuditTasksLoading() {
    return this.auditTasksLoading.asObservable();
  }
  updateAuditTasksLoading(loading: boolean) {
    this.auditTasksLoading.next(loading);
  }

  private selectedAuditTask = new BehaviorSubject(null);
  getSelectedAuditTask() {
    return this.selectedAuditTask.asObservable();
  }
  updateSelectedAuditTask(task: any) {
    this.router.navigate([], {
      queryParams: {
        taskId: task?.taskId,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    this.selectedAuditTask.next(task);
  }

  getAuditGeneral(data: any): Observable<any> {
    return this.http.post('/keywords/get-audit-general', data, {
      context: new HttpContext().set(USE_SEO_BASE, true),
    });
  }

  getAuditTask(taskId?: string): Observable<any> {
    const email: string = this.localStorageService.get('email') || '';
    const isBehamicsUser = email?.endsWith('@behamics.com') ? true : false;
    const target = this.targetService.getSelectedTarget('target');

    let params = `target=${target}`;

    if (isBehamicsUser) {
      params += `&behamicsUser=${isBehamicsUser}`;
    }

    if (!taskId) {
      return this.http.get(`/audit-url/tasks?${params}`, {
        context: new HttpContext().set(FORCE_REGION, 'west-eu'),
      });
    }
    return this.http.get(`/audit-url/tasks/${taskId}?${params}`, {
      context: new HttpContext().set(FORCE_REGION, 'west-eu'),
    });
  }

  addAuditTask(task: any): Observable<any> {
    const email: string = this.localStorageService.get('email') || '';
    const isBehamicsUser = email?.endsWith('@behamics.com') ? true : false;
    const target = this.targetService.getSelectedTarget('target');

    let params = `target=${target}`;

    if (isBehamicsUser) {
      params += `&behamicsUser=${isBehamicsUser}`;
    }

    return this.http.post(`/audit-url/tasks?${params}`, task, {
      context: new HttpContext().set(FORCE_REGION, 'west-eu'),
    });
  }

  rerunAudit(data: any): Observable<any> {
    const email: string = this.localStorageService.get('email') || '';
    const isBehamicsUser = email?.endsWith('@behamics.com') ? true : false;
    const target = this.targetService.getSelectedTarget('target');

    let params = `target=${target}`;

    if (isBehamicsUser) {
      params += `&behamicsUser=${isBehamicsUser}`;
    }

    return this.http.post(`/audit-url/tasks/runAudit?${params}`, data, {
      context: new HttpContext().set(FORCE_REGION, 'west-eu'),
    });
  }

  deleteAudit(taskId: string): Observable<any> {
    const email: string = this.localStorageService.get('email') || '';
    const isBehamicsUser = email?.endsWith('@behamics.com') ? true : false;
    const target = this.targetService.getSelectedTarget('target');

    let params = `target=${target}`;

    if (isBehamicsUser) {
      params += `&behamicsUser=${isBehamicsUser}`;
    }

    return this.http.delete(`/audit-url/tasks?taskId=${taskId}&${params}`, {
      context: new HttpContext().set(FORCE_REGION, 'west-eu'),
    });
  }

  getAuditTaskErrors(data: any): Observable<any> {
    const email: string = this.localStorageService.get('email') || '';
    const isBehamicsUser = email?.endsWith('@behamics.com') ? true : false;
    const target = this.targetService.getSelectedTarget('target');

    data['target'] = target;
    data['behamicsUser'] = isBehamicsUser;

    let params: string = '';

    Object.keys(data).forEach((key, index) => {
      const value = data[key];
      if (value !== null && value !== undefined) {
        params += `${index === 0 ? '' : '&'}${key}=${value}`;
      }
    });

    return this.http.get(`/audit-url/tasks/errors?${params}`, {
      context: new HttpContext().set(FORCE_REGION, 'west-eu'),
    });
  }

  getAuditTaskShareableToken(data: any): Observable<any> {
    let params: string = '';

    Object.keys(data).forEach((key, index) => {
      const value = data[key];
      if (value !== null && value !== undefined) {
        params += `${index === 0 ? '' : '&'}${key}=${value}`;
      }
    });

    return this.http.post(
      `/audit-url/tasks/enable-share?${params}`,
      {},
      {
        context: new HttpContext().set(FORCE_REGION, 'west-eu'),
      },
    );
  }

  getAuditTaskFromToken(token: string): Observable<any> {
    return this.http.get(`/audit-url/tasks/share?token=${token}`, {
      context: new HttpContext().set(FORCE_REGION, 'west-eu'),
    });
  }
}
